// The top of a list page: a serif headline, one readable line saying what
// the list is, then the row of controls that picks the list (with an
// optional note at its right end). Shared by the homepage and /storylines
// so the two can't drift apart in size or spacing (2026-09-28); the list
// below should start with LIST_TOP.

export const LIST_TOP = 'mt-5 border-t-[3px] border-foreground'

export default function PageHeader({
  title,
  subtitle,
  controls,
  aside,
  size = 'page',
  icon = null,
}) {
  // The identity's masthead (2026-11-01, the mocks): the title and the line
  // under it centred, a newspaper section front. The three index pages
  // (Storylines, Topics, Sources) pass size="index" for the 56px title;
  // every other page, the homepage's date included, is 44px.
  const titleSize =
    size === 'index'
      ? 'text-[34px] sm:text-[56px] sm:leading-[1.05]'
      : 'text-[30px] sm:text-[44px]'
  return (
    <header>
      {/* The kind's symbol over an overview page's name (the concept,
          2026-11-01): Storylines' three chapters, Topics' circle. */}
      {icon && <div className="mb-2.5 flex justify-center">{icon}</div>}
      <h1
        className={`text-center font-serif font-medium leading-tight ${titleSize}`}
      >
        {title}
      </h1>
      {subtitle && (
        <p className="mx-auto mt-2 max-w-[640px] text-center font-serif text-[17px] leading-normal text-secondary-foreground sm:mt-3 sm:text-[19px]">
          {subtitle}
        </p>
      )}
      {(controls || aside) && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          {controls}
          {aside}
        </div>
      )}
    </header>
  )
}

// A section of a detail page (story, storyline, source), 2026-09-28: a serif
// heading in sentence case, the way a newspaper heads a section, instead of
// the small-caps eyebrow the story page used to hang over every block.
// `aside` sits at the right end of the heading's line (a count, a link).
// `rule` draws a full-width hairline under it (the story page's sections,
// 2026-10-23); 'md' only from tablet width up, where a section's own rule
// (the Headlines tabs on a phone) would otherwise double it.
export function SectionHead({ children, aside, className = '', rule = false }) {
  const ruled =
    rule === 'md'
      ? 'md:border-b md:border-border md:pb-2'
      : rule
        ? 'border-b border-border pb-2'
        : ''
  return (
    <div
      className={`mb-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 ${ruled} ${className}`}
    >
      <h2 className="font-serif text-xl font-medium tracking-tight text-foreground sm:text-[22px]">
        {children}
      </h2>
      {aside}
    </div>
  )
}

// The pill switch every list uses. options: [{ id, label, dot?: tailwind bg
// class, title? }]. The identity's grouped pill (2026-10-31): an ink outline
// on paper, the selected option filled ink, or filled with its lean colour
// when it carries one (Left, Right). Lean colour marks data, so it fills
// only the option that IS a lean. `size="sm"` for a control that sits beside
// a section heading (article order).
export function Pills({ options, value, onChange, label, size = 'md' }) {
  const pad =
    size === 'sm' ? 'min-h-8 px-3 text-xs' : 'min-h-10 px-4 text-[13px]'
  return (
    <div
      role="group"
      aria-label={label}
      className="inline-flex shrink-0 gap-[3px] rounded-full border border-foreground bg-card p-[3px]"
    >
      {options.map((o) => {
        const on = value === o.id
        const fill = on
          ? o.dot
            ? `${o.dot} text-white`
            : 'bg-foreground text-background'
          : ''
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            aria-pressed={on}
            title={o.title}
            className={`inline-flex items-center gap-1.5 rounded-full font-sans font-semibold transition-colors ${pad} ${
              on ? fill : 'text-foreground hover:bg-muted'
            }`}
          >
            {o.dot && (
              <span
                className={`h-2 w-2 rounded-full ${on ? 'bg-white' : o.dot}`}
                aria-hidden="true"
              />
            )}
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

// The small menu beside a list or chart (2026-10-22): a second choice that
// would crowd the row as another set of pills (a topic's kind, a chart's
// view). options: [{ id, label }].
export function Menu({ options, value, onChange, label }) {
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="min-h-10 shrink-0 cursor-pointer rounded-full border border-foreground bg-card px-4 py-1.5 font-sans text-[13px] font-semibold text-foreground"
    >
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.label}
        </option>
      ))}
    </select>
  )
}
